import { Globe, MapPin } from "lucide-react";
import type { Quizrun } from "@/types/domain";
import { isPastEvent } from "@/lib/events";
import { safeHref } from "@/lib/utils";
import { formatPrice } from "@/lib/currency";
import { isVirtualLocationId } from "@/lib/virtual";

interface LocationDisplayProps {
  quizrun: Quizrun;
  compact?: boolean;
  /** Show the per-location price tag. Off on the Events browse cards where it's
   *  repetitive noise; price still shows on register/detail surfaces. */
  showPrice?: boolean;
}

export const LocationDisplay = ({ quizrun, compact = false, showPrice = true }: LocationDisplayProps) => {
  const isFinished = isPastEvent(quizrun);
  const eventsWithLocation = quizrun.events
    .filter((e) => e.location)
    // Earliest date first; Virtual always sinks to the end (it's online, not a
    // place to travel to — city events are the real draw).
    .sort((a, b) => {
      const av = isVirtualLocationId(a.location_id);
      const bv = isVirtualLocationId(b.location_id);
      if (av !== bv) return av ? 1 : -1;
      return a.date_start.localeCompare(b.date_start);
    });
  if (eventsWithLocation.length === 0) return null;

  const MAX_SHOWN = compact ? 2 : eventsWithLocation.length;
  const shown = eventsWithLocation.slice(0, MAX_SHOWN);
  const remaining = eventsWithLocation.length - MAX_SHOWN;

  // Past events: plain text cities
  if (isFinished) {
    const cities = shown.map((e) => e.location.city).filter(Boolean);
    const label = remaining > 0 ? `${cities.join(" · ")} +${remaining}` : cities.join(" · ");
    return <span className="text-muted-foreground text-xs">{label}</span>;
  }

  return (
    <span className="text-xs flex flex-wrap items-center gap-1.5">
      {shown.map((e) => {
        const city = e.location.city;
        const isVirtual = isVirtualLocationId(e.location_id);
        const Icon = isVirtual ? Globe : MapPin;
        const venue =
          e.location.location_name && e.location.location_name !== city
            ? e.location.location_name
            : null;
        const mapHref = isVirtual ? undefined : safeHref(e.location.location_link);

        const chipClass =
          "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground";
        const price = formatPrice(e.price_cents, e.price_currency);
        const inner = (
          <>
            <Icon className="h-3 w-3 shrink-0" />
            <span>{city}</span>
            {/* Venue hidden on mobile — city alone keeps the chips short and
                scannable; full venue returns from sm up. */}
            {venue && <span className="hidden sm:inline opacity-70">· {venue}</span>}
            {/* Price as a quiet trailing tag inside the chip — kept short so the
                row stays scannable; "Pay what you want" reflects the tiered model. */}
            {showPrice && (
              <span className="ml-1 pl-1.5 border-l border-muted-foreground/25 opacity-90">
                {price}
              </span>
            )}
          </>
        );
        if (mapHref) {
          return (
            <a
              key={e.location_id}
              href={mapHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(ev) => ev.stopPropagation()}
              className={`${chipClass} hover:bg-muted hover:text-foreground transition-colors`}
              title="Open meeting point in Google Maps"
            >
              {inner}
            </a>
          );
        }

        return (
          <span key={e.location_id} className={chipClass}>
            {inner}
          </span>
        );
      })}
      {remaining > 0 && <span className="text-muted-foreground">+{remaining}</span>}
    </span>
  );
};
